// @ts-strict-ignore
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  DropdownMenu,
  Icon,
  IconName,
  Tooltip,
} from '@usealloy/component-library';
import { getProperty } from 'dot-prop';
import styled from 'styled-components';
import type { ConnectDragSource } from 'react-dnd';

import { isRuleObjectIndexedRule } from 'flow/service/thresholds/thresholds';
import type { BooleanRule, IndexedRule, RuleObject } from 'flow/store/engine/types';
import { ActionType } from '~/dashboard/views/workflows/store/engine/constants';
import { isActionOfType } from '~/dashboard/views/workflows/service/actions';

import { UserContext } from '~/dashboard/context';
import { createStringOfPathArray } from '~/dashboard/views/workflows/store/engine/utils';
import { selectIsEditingLinkedChildAction } from '~/dashboard/views/workflows/store/redux/selectors';
import {
  insertNewThresholdToRules,
  removeThreshold,
  setSelectedThresholdSimThresholdId,
  updateAction,
  updateActionRulesGrouping,
  updateLinkedChildActionThresholdIds,
} from '~/dashboard/views/workflows/store/redux/slice';
import { selectThresholdSimParamsByPublicId } from '~/flow/thresholdSimulator/redux/selectors';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import { NewLineInsertionPathMode, NewLineInsertionPosition } from '../../../../store/engine/update/rules/constants';
import { RuleEditorContext } from '../context';
import {
  getFlattenedPathAsArray,
  getRuleItemPathFromFlattenedPath,
  getSingleThresholdRulePath,
  getRulePathIndexMap,
  getRulePathInDotNotationFormat,
} from './helpers';
import { RuleLineNumber } from '../styles';
import { CaretPositionSuffixTypes, RuleEditorDropdownMode } from '../types';
import { useEditorAccess } from 'flow/editor/context';
import { getIsEditable } from 'flow/service/general';
import { selectSelectedThresholdSimThresholdId, selectThresholdSimDisabledReason } from 'flow/store/redux/selectors/workflowFeatures';
import { getThresholdSimDisabledMessage } from './RuleBuilderContainer/ThresholdSimulatorModal/helpers';
import ThresholdSimulatorModal from './RuleBuilderContainer/ThresholdSimulatorModal';

const RuleLineTooltip = styled(Tooltip)`
  font-size: var(--cl-font-size-1);
  line-height: var(--cl-line-height-1);
  text-align: center;
  max-width: 210px;
`;

const RuleLine = ({
  drag,
  isLineDraggable,
  lineObject,
  lineIndex,
  setRuleItemHighlighted,
  totalNumRuleLines,
  parentRulePath,
}: {
  drag: ConnectDragSource;
  isLineDraggable: boolean;
  lineObject: {
    id: string | number;
    items: string[];
  };
  lineIndex: number;
  setRuleItemHighlighted: (isRuleItemHighlighted: boolean) => void;
  totalNumRuleLines: number;
  parentRulePath: string;
}) => {
  const dispatch = useAppDispatch();
  const { featureFlags } = useContext(UserContext);
  const isEditingLinkedChildAction = useAppSelector(selectIsEditingLinkedChildAction);

  const {
    sourceAction,
    semanticLineBreakData,
    containsAmbiguousRuleOperator,
    isDragging,
    pendingGroupOpeningParenthesisInfo,
    setPendingGroupOpeningParenthesisInfo,
  } = useContext(RuleEditorContext);

  const [activeLine, setActiveLine] = useState<number>(undefined);
  const [activeThreshold, setActiveThreshold] = useState<string>(undefined);

  const selectedThresholdSimId = useAppSelector(selectSelectedThresholdSimThresholdId);
  const thresholdSimParams = useAppSelector(selectThresholdSimParamsByPublicId(activeThreshold));
  const thresholdSimDisabledReason = useAppSelector(
    state => selectThresholdSimDisabledReason(activeThreshold)(state),
  );

  const { editorMode } = useEditorAccess();
  const inEditMode = getIsEditable(editorMode);

  const lineIndices = semanticLineBreakData?.groupedByLines[activeLine]?.items;

  // TODO: Remove feature flag when ready
  const lineSupportsThresholdSim = featureFlags?.thresholdSimulator && activeThreshold;
  const thresholdSimDisabledMessage = (thresholdSimDisabledReason && thresholdSimParams)
    ? getThresholdSimDisabledMessage(thresholdSimDisabledReason, thresholdSimParams)
    : null;

  useEffect(() => {
    if (activeLine !== undefined) {
      const rulePath = getSingleThresholdRulePath(lineIndices);
      // If there is only one rule, `sourceAction.rules` will be an object with a single key of id
      const ruleObject = (
        (sourceAction.rules as IndexedRule).id
          ? sourceAction.rules
          : getProperty(sourceAction.rules, rulePath)
      ) as IndexedRule;
      setActiveThreshold(ruleObject.id);
    }
  }, [activeLine, lineIndices, sourceAction.rules]);

  // if line has an item either ending with groupStart or groupEnd, we don't offer actions like delete (yet)
  const activeLineHasGroupParenthesis = useMemo(() => {
    const endsWithGroupStart = lineIndices?.find(
      item => item.endsWith(CaretPositionSuffixTypes.GROUP_START));
    const endsWithGroupEnd = lineIndices?.find(
      item => item.endsWith(CaretPositionSuffixTypes.GROUP_END));

    if (endsWithGroupStart) return CaretPositionSuffixTypes.GROUP_START;
    if (endsWithGroupEnd) return CaretPositionSuffixTypes.GROUP_END;
    return false;
  }, [lineIndices]);

  const onAddLine = (position: NewLineInsertionPosition) => {
    if (!activeLineHasGroupParenthesis) {
      // in this case, the line is about a threshold, therefore grabbing a second item would give us the flattened path
      // why second item in this line? because the first item could be and/or operator if this is not the first line.
      // and there's always at least 2 items in a line that represents a threshold.
      const targetItemPath = lineIndices[1] ?? lineIndices[0];
      if (targetItemPath.includes(CaretPositionSuffixTypes.RULE_OPERATOR)) return false;
      const targetItemFlattenedPath = getRuleItemPathFromFlattenedPath(targetItemPath);
      const flattenedPathInArray = getFlattenedPathAsArray(targetItemFlattenedPath);
      const flattenedPathInArrayWithoutLastItem = position === NewLineInsertionPosition.BELOW
        ? [
          ...flattenedPathInArray.slice(0, flattenedPathInArray.length - 2),
          String(Number(flattenedPathInArray.at(-2)) + 1),
        ]
        : flattenedPathInArray
          .slice(0, flattenedPathInArray.length - 1); // last item in this path would be extra -0, -1, ... for dropdown mapping. in here, we only need the threshold location as in path
      const rulePath = createStringOfPathArray(flattenedPathInArrayWithoutLastItem);

      dispatch(insertNewThresholdToRules({
        actionId: sourceAction.publicId,
        insertOrder: position,
        rulePathToInsert: rulePath,
      }));
    } else {
      let line = lineIndices;
      let mode;
      switch (position) {
        case NewLineInsertionPosition.ABOVE: {
          // we grab previous line which is the last line of this group scope
          if (activeLine > 0) {
            line = semanticLineBreakData?.groupedByLines[activeLine - 1]?.items;
            const linesTwoIndexBefore = semanticLineBreakData
              ?.groupedByLines[activeLine - 2]?.items;
            if (linesTwoIndexBefore
              && linesTwoIndexBefore.at(-1).endsWith(CaretPositionSuffixTypes.GROUP_START)) {
              // this means this group scope only has 1 item in it
              mode = NewLineInsertionPathMode.FIRST_ITEM_ABOVE_FIRST_GROUP;
            }
          } else {
            mode = NewLineInsertionPathMode.FIRST_ITEM_ABOVE_FIRST_GROUP;
          }
          break;
        }
        case NewLineInsertionPosition.BELOW: {
          if (activeLineHasGroupParenthesis === CaretPositionSuffixTypes.GROUP_START) {
            // we grab next line which is the first line of this group scope
            line = semanticLineBreakData?.groupedByLines[activeLine + 1]?.items;
            mode = NewLineInsertionPathMode.FIRST_IN_GROUP;
          } else {
            mode = NewLineInsertionPathMode.AFTER_GROUP;
          }
          break;
        }
        default: {
          // do nothing
        }
      }
      const targetItemPath = line[0]; // in this case, we have to grab the first item in the line because it may not have 2nd item if the line is just `)`.
      const targetItemFlattenedPath = getRuleItemPathFromFlattenedPath(targetItemPath);
      const flattenedPathInArray = getFlattenedPathAsArray(targetItemFlattenedPath);

      let rulePathArray;
      switch (mode) {
        case NewLineInsertionPathMode.FIRST_IN_GROUP: {
          rulePathArray = flattenedPathInArray.slice(0, flattenedPathInArray.length - 2);
          break;
        }
        case NewLineInsertionPathMode.AFTER_GROUP: {
          rulePathArray = [
            ...flattenedPathInArray.slice(0, flattenedPathInArray.length - 1),
            String(Number(flattenedPathInArray.at(-1)) + 1),
          ];
          break;
        }
        case NewLineInsertionPathMode.FIRST_ITEM_ABOVE_FIRST_GROUP: {
          rulePathArray = flattenedPathInArray
            .slice(0, flattenedPathInArray.length - 1);
          break;
        }
        default: {
          const ruleLastItemIncrements = Number(flattenedPathInArray.at(-1)) + 1;
          rulePathArray = [
            ...flattenedPathInArray.slice(0, flattenedPathInArray.length - 1),
            String(ruleLastItemIncrements),
          ];
        }
      }
      const rulePath = createStringOfPathArray(rulePathArray);

      dispatch(insertNewThresholdToRules({
        actionId: sourceAction.publicId,
        insertOrder: position,
        rulePathToInsert: rulePath,
      }));
    }

    return true;
  };

  const onDeleteLine = () => {
    // we currently do not support whole group deletion
    if (!activeLineHasGroupParenthesis) {
      // in this case, the line is about a threshold, therefore grabbing a second item would give us the flattened path
      // why second item in this line? because the first item could be and/or operator if this is not the first line.
      // and there's always at least 2 items in a line that represents a threshold.
      const rulePath = getSingleThresholdRulePath(lineIndices);
      const ruleObject = (
        getProperty(sourceAction.rules, rulePath)
        ?? sourceAction.rules
      ) as RuleObject;

      if (isRuleObjectIndexedRule(ruleObject)) {
        dispatch(removeThreshold({
          id: ruleObject.id,
        }));

        if (isEditingLinkedChildAction) {
          dispatch(updateLinkedChildActionThresholdIds({
            operation: 'remove',
            thresholdId: ruleObject.id,
          }));
        }
      }
    }
  };

  const showCreateGroupOption = !activeLineHasGroupParenthesis // not a line with a parenthesis
    && !containsAmbiguousRuleOperator // not an ambiguous rule
    && !pendingGroupOpeningParenthesisInfo.path; // not in the middle of opening a group

  const onCreateOpenGroup = () => {
    if (totalNumRuleLines === 1) {
      dispatch(updateAction({
        path: `${sourceAction.publicId}.rules`,
        value: (
          // NOTE: If we have `sourceAction.rules.and` here it means we previously
          // had grouped a single rule and then ungrouped it; the existing logic
          // for `onRemoveGroup` (which calls `updateActionRulesGrouping`)
          // doesn't expect this and so we are remedying that here
          // (versus in `onRemoveGroup`) so that this logic for
          // "when you're grouping only one thing" stays in one place and
          // doesn't spread to any other functions
          !(typeof sourceAction.rules === 'string') && (sourceAction.rules as BooleanRule)?.and
            ? { and: [sourceAction.rules] }
            : { and: [{ and: [sourceAction.rules] }] }
        ),
      }));
    } else {
      const targetIndexMap = getRulePathIndexMap();
      // you can only open groups when the mode is Threshold Left Action:
      const targetIndex = targetIndexMap[RuleEditorDropdownMode.THRESHOLD_LEFT_ACTION];

      setPendingGroupOpeningParenthesisInfo({ path: `${parentRulePath}[${targetIndex}]` });
    }
  };

  const showRemoveGroupOption = activeLineHasGroupParenthesis
    && !pendingGroupOpeningParenthesisInfo.path;

  const onRemoveGroup = () => {
    const groupParenthesisPath = getRulePathInDotNotationFormat({
      itemFlatCaret: lineIndices[0],
      sourceAction,
    });
    dispatch(updateActionRulesGrouping({
      id: sourceAction.publicId,
      groupParenthesisPath,
    }));
  };


  const tooltipMessage = useMemo(() => {
    if (containsAmbiguousRuleOperator) return 'Please take an action to correct ambiguous logic.';
    return isLineDraggable ? (<>Hold to drag<br />Click to view menu</>) : 'Click to view menu';
  }, [containsAmbiguousRuleOperator, isLineDraggable]);

  const shouldShowDeleteOption = (
    !activeLineHasGroupParenthesis
    && !pendingGroupOpeningParenthesisInfo.path
    // can't delete the last threshold of tag, outcomes, or new linked action
    && !(totalNumRuleLines <= 1 && (
      isEditingLinkedChildAction
      || isActionOfType(sourceAction, [ActionType.OUTCOME, ActionType.TAG])
    ))
  );

  return (
    <div key={lineObject.id}>
      <RuleLineTooltip hide={!inEditMode} content={tooltipMessage}>
        <RuleLineNumber
          data-dd-action-name={`rule-line-number-${lineIndex}`}
          ref={isLineDraggable ? drag : null}
          onClick={() => (!containsAmbiguousRuleOperator && inEditMode
            ? setActiveLine(lineIndex) : {})}
          onMouseEnter={() => { if (!isDragging && isLineDraggable) setRuleItemHighlighted(true); }}
          onMouseLeave={() => setRuleItemHighlighted(false)}
          active={activeLine === lineIndex}
          isDraggable={isLineDraggable}
        >
          {lineIndex + 1}
        </RuleLineNumber>
      </RuleLineTooltip>
      <DropdownMenu
        trigger={<div />}
        isOpen={activeLine === lineIndex}
        onOpenChange={isNowOpen => { if (!isNowOpen) setActiveLine(undefined); }}
      >
        <DropdownMenu.Item onSelect={() => onAddLine(NewLineInsertionPosition.ABOVE)} data-dd-action-name="line-number-menu-add-above">
          <Icon icon={IconName.ArrowUp} />
          Add above
        </DropdownMenu.Item>
        <DropdownMenu.Item onSelect={() => onAddLine(NewLineInsertionPosition.BELOW)} data-dd-action-name="line-number-menu-add-below">
          <Icon icon={IconName.ArrowDown} />
          Add below
        </DropdownMenu.Item>
        {showCreateGroupOption && (
          <DropdownMenu.Item
            data-dd-action-name="line-number-menu-create-group"
            onSelect={onCreateOpenGroup}
          >
            <Icon icon={IconName.AddLayer} />
            Open Group (
          </DropdownMenu.Item>
        )}
        {showRemoveGroupOption && (
          <DropdownMenu.Item
            data-dd-action-name="line-number-menu-remove-group"
            onSelect={onRemoveGroup}
          >
            <Icon icon={IconName.Ungroup} />
            Ungroup
          </DropdownMenu.Item>
        )}
        {lineSupportsThresholdSim && (
          <RuleLineTooltip
            content={thresholdSimDisabledMessage}
            hide={!thresholdSimDisabledReason}
            side="top"
          >
            <DropdownMenu.Item
              data-dd-action-name="line-number-menu-threshold-simulator"
              onSelect={() => {
                dispatch(setSelectedThresholdSimThresholdId(activeThreshold));
              }}
              disabled={Boolean(thresholdSimDisabledReason)}
            >
              <Icon icon={IconName.Evaluations} />
              Threshold Simulator
            </DropdownMenu.Item>
          </RuleLineTooltip>
        )}
        {shouldShowDeleteOption && (
          <DropdownMenu.Item
            data-dd-action-name="line-number-menu-delete"
            onSelect={onDeleteLine}
            color="var(--cl-red-500)"
          >
            <Icon icon={IconName.Trash} color="var(--cl-red-500)" />
            Delete
          </DropdownMenu.Item>
        )}
      </DropdownMenu>
      {lineSupportsThresholdSim && selectedThresholdSimId === activeThreshold && (
        <ThresholdSimulatorModal
          isOpen
          onClose={() => dispatch(setSelectedThresholdSimThresholdId(null))}
        />
      )}
    </div>
  );
};

export default RuleLine;
